import { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react';
import { Status } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { Button } from '../Button';
import { FormLabel, FormMessage } from '../common';
import { Stack } from '../Stack';
import { FileContainer } from './FileContainer';
import { areAllFilesAccepted, cancelEvent, createFileList, filterUniqueFiles } from './helpers';
import { HelpTooltip } from './HelpTooltip';
import {
    DropzoneContainer,
    DropzoneText,
    InvisibleInput,
    RightTopPositioner,
    UploadIcon,
    UploadTextWrapper,
} from './style';
import { DropzoneEvent, DropzoneProps } from './types';

export const Dropzone = ({
    accept,
    disabled,
    error,
    filesAreNotSupportedErrorMessage,
    helper,
    id,
    label,
    maxFilesLimit,
    multiple,
    multipleFileDropNotAllowedErrorMessage,
    name,
    onChange,
    required,
    uploadFilesText,
    value,
    ...other
}: DropzoneProps) => {
    const inputElement = useRef<HTMLInputElement>(null);
    const { t } = useLocalization();

    const [fileList, setFileList] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);

    // In PMS the value is lost after form submission but the page is
    // not reload hence it is need to sync empty state
    useEffect(() => {
        if (!value && fileList.length && inputElement.current?.value) {
            setFileList([]);
            inputElement.current.value = '';
        }
    }, [value, fileList, setFileList]);

    const handleDrag = (event: DragEvent<HTMLDivElement>, type: 'over' | 'leave') => {
        cancelEvent(event);
        setIsDragging(type === 'over');
    };

    const handleFiles = (event: ChangeEvent<HTMLInputElement> | DragEvent<HTMLDivElement>, files: FileList) => {
        const newFiles = Array.from(files);
        const combinedFiles = filterUniqueFiles([...fileList, ...newFiles]);
        setFileList(combinedFiles);
        saveFiles(event, createFileList(combinedFiles));
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        cancelEvent(event);

        setIsDragging(false);
        const { files } = event.dataTransfer;
        if (inputElement.current) {
            inputElement.current.files = files;
        }

        if (files?.length) handleFiles(event, files);
    };

    const handleClick = () => {
        if (inputElement.current) {
            inputElement.current.click();
        }
    };

    const clearFile = (removeFile: File) => {
        if (!value) {
            return;
        }

        const files = value.filter((file) => file !== removeFile);

        const newFileList = createFileList(files);
        if (inputElement.current) {
            inputElement.current.files = newFileList;
        }

        setFileList(files);
        saveFiles(null, newFileList);
    };

    const saveFiles = (event: DropzoneEvent, files: FileList) => {
        onChange(event, Array.from(files));
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (files?.length) handleFiles(event, files);
    };

    const filesAmount = value?.length || 0;

    const limitReached = Boolean(maxFilesLimit && filesAmount && filesAmount === maxFilesLimit);
    const limitOverReached = maxFilesLimit ? filesAmount > maxFilesLimit : false;
    const isSupportedFileFormat = accept && value ? areAllFilesAccepted(value, accept) : true;
    const isMultipleFileUpload = value && filesAmount > 1;

    const finalError =
        error ||
        (!isSupportedFileFormat && filesAreNotSupportedErrorMessage) ||
        (isMultipleFileUpload && !multiple && multipleFileDropNotAllowedErrorMessage) ||
        (limitOverReached &&
            t(OptimusTextKey.DropzoneFileLimitOverReachedError, {
                maxFilesLimit: maxFilesLimit || 0,
                filesOverLimitAmount: filesAmount - (maxFilesLimit || 0),
            }));

    return (
        <Stack {...other} spacing="25" vertical>
            {label ? (
                <FormLabel required={required} $disabled={disabled}>
                    {label}
                </FormLabel>
            ) : null}
            <DropzoneContainer
                data-test-dropzone
                disabled={disabled || limitReached}
                error={!!finalError}
                isDragging={isDragging}
                onDragOver={(e) => handleDrag(e, 'over')}
                onDragLeave={(e) => handleDrag(e, 'leave')}
                onDrop={handleDrop}
            >
                <UploadIcon
                    size="medium"
                    value={IconName.Upload}
                    variant="inherit"
                    error={!!finalError}
                    disabled={disabled || limitReached}
                />
                <UploadTextWrapper>
                    <DropzoneText error={!!finalError} disabled={disabled || limitReached}>
                        {uploadFilesText}
                    </DropzoneText>
                    <DropzoneText error={!!finalError} disabled={disabled || limitReached}>
                        {t(OptimusTextKey.Or)}
                    </DropzoneText>
                </UploadTextWrapper>
                <Button
                    disabled={disabled || limitReached}
                    onClick={handleClick}
                    size="small"
                    type="button"
                    variant="secondary"
                >
                    {t(OptimusTextKey.ChooseFile)}
                </Button>
                {helper ? (
                    <RightTopPositioner disabled={disabled || limitReached}>
                        <HelpTooltip tooltip={helper} />
                    </RightTopPositioner>
                ) : null}
            </DropzoneContainer>
            <InvisibleInput
                type="file"
                ref={inputElement}
                onChange={handleFileChange}
                accept={accept}
                id={id}
                name={name}
                multiple={multiple}
                disabled={disabled || limitReached}
            />
            {value ? value.map((file) => <FileContainer key={file.name} clearFile={clearFile} file={file} />) : null}
            {limitReached && (
                <FormMessage>
                    {t(OptimusTextKey.DropzoneFileLimitReached, { maxFilesLimit: maxFilesLimit || 0 })}
                </FormMessage>
            )}
            {finalError ? <FormMessage status={Status.Error}>{finalError}</FormMessage> : null}
        </Stack>
    );
};
